import { useEffect, useRef } from 'react'
import { useAppSelector } from '../../store'
import { InAppNotificationType } from 'pure-js/enums/InAppNotificationType'
import type { GameReminderPayload } from 'pure-js/types/InAppNotification'
import { selectInAppNotificationsList } from '../../store/selectors/inAppNotificationsSelectors'
import { isGameReminderDisabled } from '../utils/gameReminderVisibility'
import { useMarkNotificationViewed } from '../../apiLayer/queryHooks/mutations'

const CHECK_INTERVAL_MS = 30 * 1000

/**
 * Automatically marks GAME_REMINDER notifications as viewed once they are past the
 * "disabled" threshold (3 minutes after the game's scheduled start). This keeps the
 * badge and "action required" state accurate: expired reminders no longer count as
 * unread. Single source of truth (backend viewedAt) so state is correct across
 * devices and after refresh. Mount once when user is logged in (e.g. InAppNotificationsMount).
 */
export function useAutoMarkExpiredGameReminders() {
  const notifications = useAppSelector(selectInAppNotificationsList)
  const { mutate: markViewed } = useMarkNotificationViewed()
  const autoViewedIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const runCheck = () => {
      const now = Date.now()
      notifications.forEach((n) => {
        if (n.type !== InAppNotificationType.GAME_REMINDER || n.viewedAt || autoViewedIdsRef.current.has(n.id)) {
          return
        }
        const payload = n.payload as GameReminderPayload
        if (!isGameReminderDisabled(payload, now)) return

        autoViewedIdsRef.current.add(n.id)
        markViewed(n.id)
      })
    }

    runCheck()
    const intervalId = setInterval(runCheck, CHECK_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [notifications, markViewed])
}
